import { shouldProtectRoute } from '@/helper/shouldProtectRoute';
import { getFirebaseToken } from '@/helper/getFirebaseToken';
import { auth } from '@/utilities/admin.firebase.utility';
import store from '@/store/store';
import axios, { AxiosError, AxiosRequestConfig, InternalAxiosRequestConfig } from 'axios';
import { getIdToken } from 'firebase/auth';

type RetriableRequestConfig = InternalAxiosRequestConfig & { _retry?: boolean };

/**
 * BTBS-140: a 401 must only be retried with a fresh token when the
 * request interceptor had already decided this destination may receive the
 * user's identity token (it sent an `Authorization` header the first time).
 * Retrying every 401 — including ones from third-party hosts, or requests
 * deliberately sent without the token — would hand the token to a foreign
 * origin. Header casing varies (plain object vs AxiosHeaders), so check both.
 */
export const shouldRetryWithFreshToken = (error: AxiosError): boolean => {
  const originalRequest = error.config as RetriableRequestConfig | undefined;

  if (error.response?.status !== 401) return false;
  if (!originalRequest || originalRequest._retry) return false;

  const headers = originalRequest.headers as Record<string, unknown> | undefined;
  const hadAuthorization = Boolean(headers?.['Authorization'] ?? headers?.['authorization']);

  return hadAuthorization;
};

// BT-1380: identify the acting admin for backend audit logging (session_logs).
// Mirrors bonum-webapp's applyActorHeaders. Never throws — a header failure
// must not break the request.
//
// This is the internal CX/admin dashboard, so every actor mutating a session
// here is an admin. We send x-actor-role: 'admin' explicitly; otherwise the
// backend audit log defaults the actor to 'coachee' and misattributes the
// action. We also forward x-actor-id and x-actor-email so the log carries the
// real acting user's identity.
const applyActorHeaders = (headers: Record<string, string>): Record<string, string> => {
  try {
    const next: Record<string, string> = {
      ...headers,
      'x-app-source': 'dashboard',
      'x-actor-role': 'admin',
    };
    const user = store.getState()?.user;
    if (user?.uid) next['x-actor-id'] = user.uid;
    if (user?.email) next['x-actor-email'] = user.email;
    return next;
  } catch {
    return headers;
  }
};

const authHeaders = async (request: AxiosRequestConfig) => {
  const firebaseToken = await getFirebaseToken();

  request.headers = {
    ...request.headers,
    Authorization: `Bearer ${firebaseToken}`,
    'x-app-name': 'bonum-dashboard',
  };

  return request;
};

const updateRequestWithFirebaseToken = async (request) => {
  const firebaseToken = await getIdToken(auth.currentUser, false);
  request.headers = {
    ...request.headers,
    Authorization: `Bearer ${firebaseToken}`,
  };

  return request;
};

const updateRequestWithCalendarTokens = async (request) => {
  const firebaseToken = await getIdToken(auth.currentUser, false);
  const provider = localStorage.getItem('provider');

  const nylasProviderToken = provider;

  request.headers = {
    ...request.headers,
    Authorization: `Bearer ${firebaseToken}`,
    accessToken: nylasProviderToken,
  };

  return request;
};

const updateRequestOnCreateEvent = async (request: AxiosRequestConfig) => {
  if (request.method === 'get') return request;

  const firebaseToken = await getIdToken(auth.currentUser, false);

  const provider = localStorage.getItem('provider');

  const nylasProviderToken = provider;

  // let nylasProviderToken = user?.providers[0]?.accessToken;
  // if (user.role === "coachee") {
  //   nylasProviderToken = user?.coach?.providers[0]?.accessToken;
  // }

  request.headers = {
    ...request.headers,
    Authorization: `Bearer ${firebaseToken}`,
    accessToken: nylasProviderToken,
  };

  return request;
};

// BT-1429: nexus guards PUT /users/:id with AuthGuard (Firebase ID token
// verification). Match the exact nexus users collection endpoint only —
// `includes('/users')` would also catch unrelated legacy endpoints whose
// backends don't expect an Authorization header.
const NEXUS_USERS_BASE = `${import.meta.env.VITE_APP_NEXUS_URL}/users`;
const isNexusUserUpdate = (request: AxiosRequestConfig) => {
  if (request.method?.toLowerCase() !== 'put') return false;
  if (!request.url?.startsWith(`${NEXUS_USERS_BASE}/`)) return false;
  const suffix = request.url.slice(NEXUS_USERS_BASE.length);
  return /^\/[^/]+$/.test(suffix);
};

/**
 * BT-1467: no service in this app set a timeout, and `loadAbort`'s
 * AbortController only cancels on unmount — it never fires on its own. A
 * backend that accepted the connection and then stopped answering therefore
 * left the screen spinning forever with no error, which is half of what CX
 * reported. A ceiling turns that into a rejection the UI can actually show.
 * Generous on purpose: report generation and bulk uploads are legitimately
 * slow, and a request that already carries its own `timeout` keeps it.
 */
const DEFAULT_TIMEOUT_MS = 60_000;

axios.interceptors.request.use((request: AxiosRequestConfig) => {
  request.headers = {
    'x-app-id': 'coaching',
    ...request.headers,
  };
  if (request.timeout === undefined || request.timeout === 0) {
    request.timeout = DEFAULT_TIMEOUT_MS;
  }
  // BT-1380: only session endpoints are audited, so scope actor headers to
  // them. Sending them globally would trip CORS on services whose allow-list
  // does not include x-actor-*/x-app-source.
  if (request.url?.includes('/session')) {
    request.headers = applyActorHeaders(request.headers);
  }
  if (request.url?.includes('userRol')) return updateRequestWithFirebaseToken(request);
  if (request.url?.includes('/profiles')) return updateRequestWithFirebaseToken(request);
  if (request.url?.includes('/migrate-to-new-process'))
    return updateRequestWithFirebaseToken(request);
  if (isNexusUserUpdate(request)) return updateRequestWithFirebaseToken(request);

  if (shouldProtectRoute(request.url)) {
    return authHeaders(request);
  }

  if (
    request.url?.includes('calendars/') ||
    request.url?.includes('workscheduleUnprotected') ||
    request.url?.includes('blockedschedule')
  ) {
    return updateRequestWithCalendarTokens(request);
  }

  if (request.url?.includes('/events/nylas')) return updateRequestOnCreateEvent(request);

  if (request.url?.includes('/session')) return updateRequestWithFirebaseToken(request);

  return request;
});

let refreshInFlight: Promise<string> | null = null;

/**
 * BT-1502: share a single forced token refresh between concurrent 401s so a
 * burst of failing requests does not trigger N parallel refreshes.
 */
const refreshTokenOnce = (): Promise<string> => {
  if (!refreshInFlight) {
    refreshInFlight = getFirebaseToken(true).finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
};

/**
 * BT-1502: exact message both backends use when their auth layer rejects an
 * invalid/expired Firebase token: nexus AuthGuard
 * (`UnauthorizedException("Invalid or expired token")`) and services-users
 * `authenticate` middleware (`AuthFailureResponse('Invalid or expired token')`).
 * That rejection happens BEFORE the handler runs, so replaying the request is
 * side-effect-free even for POST/PUT/DELETE. A 401 with any other message (or
 * no JSON body) may be propagated by a handler from a downstream service after
 * side effects, and "Missing Authorization header" / "Invalid Authorization
 * header format" are not fixed by a refresh, so none of those are retried.
 */
const GUARD_EXPIRED_TOKEN_MESSAGE = 'Invalid or expired token';

const isGuardExpiredTokenError = (error: AxiosError): boolean => {
  const data = error.response?.data as { message?: unknown } | null | undefined;
  return (
    error.response?.status === 401 &&
    typeof data === 'object' &&
    data !== null &&
    data.message === GUARD_EXPIRED_TOKEN_MESSAGE
  );
};

/**
 * BT-1502 + BTBS-140: on a 401 from the auth guard (expired/invalid token),
 * force a Firebase token refresh and retry the request ONCE, and only when the
 * destination is one of our own origins (`shouldProtectRoute` compares origins,
 * not substrings) and the first attempt already carried the token
 * (`shouldRetryWithFreshToken`). The retry goes back through the request
 * interceptor, which picks up the freshly refreshed token. If the refresh or
 * the retry fails the original error propagates (no auto-logout).
 */
export const retryOn401 = async (error: AxiosError): Promise<unknown> => {
  const config = error.config as RetriableRequestConfig | undefined;
  if (
    !isGuardExpiredTokenError(error) ||
    !shouldRetryWithFreshToken(error) ||
    !config ||
    !shouldProtectRoute(config.url)
  ) {
    throw error;
  }

  config._retry = true;
  try {
    await refreshTokenOnce();
  } catch {
    throw error;
  }
  return axios(config);
};

axios.interceptors.response.use((response) => response, retryOn401);
